import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { popupAn, POPUP_SCHLUESSEL } from '@/components/NeueNachrichtenPopup';
import { superchatAPI, leadsAPI, teamAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  MessageSquare, RefreshCw, Send, Bot, Pause, Play, Loader2, Lock, Instagram, Phone, StickyNote, Check, Pencil, X,
  Bell, BellOff, Clock, Smartphone, Mail, Inbox, UserCheck, Paperclip, Search, ArrowLeft, ExternalLink, Link2, Archive, ArchiveRestore, Info, Mic, Trash2, CalendarCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ProbetrainingDialog from '@/components/ProbetrainingDialog';

/* Posteingang (Neuaufbau 18.09.2026): WhatsApp, E-Mail und Instagram in einer
   Liste — mit Klartext statt Symbolen. Links die Liste mit Suche und
   Filtern, rechts der Verlauf mit Kopfzeile (Lead öffnen/verknüpfen,
   Zuweisen, Schließen), Nachrichten mit Datumstrennern, Anhängen und
   Zustellhaken, unten der Composer mit Hinweis, wohin die Antwort geht.
   Auf dem Handy: Liste ODER Verlauf, mit Zurück-Pfeil. */

const anhangUrl = (a) => `${process.env.REACT_APP_BACKEND_URL || ''}${a.url}`;
const P1_ROT = 'hsl(var(--marke))';   // im CRM die gewählte Akzentfarbe (Neugestaltung 12.10.2026)
const SICHERHEIT_FARBE = { hoch: '#16a34a', mittel: '#d97706', niedrig: '#dc2626' };
const KANAL = {
  whatsapp: { label: 'WhatsApp', icon: Phone, cls: 'bg-emerald-600 text-white' },
  email: { label: 'E-Mail', icon: Mail, cls: 'bg-blue-600 text-white' },
  instagram: { label: 'Instagram', icon: Instagram, cls: 'bg-pink-600 text-white' },
  telefon: { label: 'Telefonnotiz', icon: Mic, cls: 'bg-amber-600 text-white' },
};
const AUFNAHME_TEXT = (g) => g?.startsWith('Stichwort') ? `Aufgenommen wegen ${g.replace('Stichwort ', 'Stichwort ')}` : g === 'Absender im CRM bekannt' ? 'Aufgenommen: Absender ist im CRM bekannt' : g ? `Aufgenommen: ${g}` : '';

const fmtZeit = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const heute = new Date().toDateString() === d.toDateString();
  return heute ? d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};
const fmtTag = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const heute = new Date(); const gestern = new Date(); gestern.setDate(heute.getDate() - 1);
  if (d.toDateString() === heute.toDateString()) return 'Heute';
  if (d.toDateString() === gestern.toDateString()) return 'Gestern';
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
};

function KanalChip({ kanal, klein }) {
  const k = KANAL[kanal] || { label: kanal, icon: MessageSquare, cls: 'bg-slate-500 text-white' };
  const Icon = k.icon;
  return <span className={`inline-flex items-center gap-1 rounded px-1.5 ${klein ? 'py-0 text-[10px]' : 'py-0.5 text-[11px]'} font-medium ${k.cls}`}><Icon className="h-3 w-3" />{k.label}</span>;
}

function Haken({ status }) {
  if (status === 'gesendet') return <span title="Gesendet" className="inline-flex"><Check className="h-3 w-3" /></span>;
  if (status === 'zugestellt') return <span title="Zugestellt" className="inline-flex -space-x-1.5"><Check className="h-3 w-3" /><Check className="h-3 w-3" /></span>;
  if (status === 'gelesen') return <span title="Gelesen" className="inline-flex -space-x-1.5 text-sky-300"><Check className="h-3 w-3" /><Check className="h-3 w-3" /></span>;
  if (status === 'fehler') return <span title="Nicht zugestellt" className="text-red-300 font-bold">!</span>;
  return null;
}

/* Lead per Suche verknüpfen — für Chats, die das CRM keiner Person zuordnen konnte. */
function LeadVerknuepfen({ konvId, onFertig }) {
  const [offen, setOffen] = useState(false);
  const [suche, setSuche] = useState('');
  const [treffer, setTreffer] = useState([]);
  useEffect(() => {
    if (!offen || suche.trim().length < 2) { setTreffer([]); return; }
    const t = setTimeout(async () => { try { const r = await leadsAPI.getAll({ search: suche.trim(), limit: 8 }); setTreffer(r.data?.leads || []); } catch { /* still */ } }, 250);
    return () => clearTimeout(t);
  }, [suche, offen]);
  const waehlen = async (lead) => {
    try { await superchatAPI.konversationUpdate(konvId, { lead_id: lead.id }); toast.success(`Mit ${lead.name} verknüpft`); setOffen(false); onFertig?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Verknüpfen fehlgeschlagen'); }
  };
  return (
    <>
      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setOffen(true)} data-testid="lead-verknuepfen"><Link2 className="h-3.5 w-3.5" /> Lead verknüpfen</Button>
      <Dialog open={offen} onOpenChange={setOffen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Chat einem Lead oder Mitglied zuordnen</DialogTitle></DialogHeader>
          <Input autoFocus value={suche} onChange={e => setSuche(e.target.value)} placeholder="Name, Telefon oder E-Mail suchen" />
          <div className="max-h-64 overflow-y-auto divide-y divide-border/50">
            {treffer.map(l => <button key={l.id} className="w-full text-left py-2 text-sm hover:bg-muted px-1 rounded" onClick={() => waehlen(l)}>{l.name} <span className="text-muted-foreground text-xs">{l.phone || l.email || ''}</span></button>)}
            {suche.trim().length >= 2 && treffer.length === 0 && <p className="text-sm text-muted-foreground py-2">Nichts gefunden.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* Probetraining buchen direkt aus dem Chat (30.10.2026): derselbe Dialog und derselbe Buchungskern wie auf den Lead- und
   Probetrainings-Seiten (schnellerfassung/buchen). Mit verknüpftem Lead wird er vorbelegt; ohne Lead legt die Buchung die Person
   über ihre Nummer an (oder erkennt sie wieder) — danach hängt der Chat an diesem Lead. */
function ProbetrainingAusChat({ konv, onFertig }) {
  const [offen, setOffen] = useState(false);
  const [vorbelegt, setVorbelegt] = useState({});
  const oeffnen = async () => {
    let lead = null;
    if (konv.lead_id) { try { lead = (await leadsAPI.get(konv.lead_id)).data; } catch { /* Name und Nummer aus dem Chat */ } }
    const name = (lead?.name || konv.name || '').trim();
    const teile = name.split(' ');
    const kind = lead && (lead.bereich === 'kinder_jugend' || /kinder|bambini|jugend/.test(lead.altersgruppe || ''));
    setVorbelegt({
      vorname: teile.length > 1 ? teile.slice(0, -1).join(' ') : name, nachname: teile.length > 1 ? teile[teile.length - 1] : '',
      telefon: lead?.phone || (konv.kanal === 'whatsapp' && konv.wa_id ? `+${konv.wa_id}` : ''), email: lead?.email || (konv.kanal === 'email' ? konv.wa_id : ''),
      zielgruppe: kind ? 'kind' : 'erwachsen', kind_vorname: kind ? '' : undefined,
    });
    setOffen(true);
  };
  const fertig = async (r) => {
    if (!konv.lead_id && r?.lead_id) {
      try { await superchatAPI.konversationUpdate(konv.id, { lead_id: r.lead_id }); toast.success('Chat mit dem Lead verknüpft'); } catch { /* Verknüpfen ist Zusatz */ }
    }
    onFertig?.(r);
  };
  return (
    <>
      <Button variant="outline" size="sm" className="gap-1.5" onClick={oeffnen} data-testid="chat-probetraining"><CalendarCheck className="h-3.5 w-3.5" /> Probetraining</Button>
      <ProbetrainingDialog offen={offen} onClose={() => setOffen(false)} onFertig={fertig} vorbelegt={vorbelegt} />
    </>
  );
}

export default function PosteingangPage() {
  const navigate = useNavigate();
  const { user, userPermissions, isAdminPermissions } = useAuth();
  const darfSenden = isAdminPermissions || !!userPermissions?.['superchat.send'];
  const istLeitung = user?.role === 'admin' || user?.role === 'manager' || isAdminPermissions;

  const [konvs, setKonvs] = useState([]);
  const [team, setTeam] = useState([]);
  const [kanalFilter, setKanalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('offen');
  const [suche, setSuche] = useState('');
  const [aktivId, setAktivId] = useState(() => new URLSearchParams(window.location.search).get('konv'));
  const ort = useLocation();
  // Pop-up „Öffnen“ bei schon offenem Posteingang: ?konv= wechselt den Verlauf (vorher nur beim ersten Laden gelesen)
  useEffect(() => { const k = new URLSearchParams(ort.search).get('konv'); if (k) setAktivId(k); }, [ort.search]);
  const [popup, setPopup] = useState(popupAn);
  const popupUmschalten = async () => {
    const an = !popup;
    try { localStorage.setItem(POPUP_SCHLUESSEL, an ? 'an' : 'aus'); } catch { /* privates Fenster */ }
    setPopup(an);
    if (an && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try { await Notification.requestPermission(); } catch { /* ältere Browser */ }
    }
    toast.success(an ? 'Pop-up bei neuen Nachrichten an (auf diesem Gerät)' : 'Pop-up bei neuen Nachrichten aus (auf diesem Gerät)');
  };
  const [detail, setDetail] = useState(null);
  const [laden, setLaden] = useState(true);
  const [text, setText] = useState('');
  const [datei, setDatei] = useState(null);
  const [sendet, setSendet] = useState(false);
  const [holtMails, setHoltMails] = useState(false);
  const [ordnetZu, setOrdnetZu] = useState(false);
  const [bearbeiteVorschlag, setBearbeiteVorschlag] = useState(null);
  const [notizDialog, setNotizDialog] = useState(false);
  const [notizText, setNotizText] = useState('');
  const endeRef = useRef(null);
  const dateiRef = useRef(null);

  const ladeListe = useCallback(async () => {
    try { const r = await superchatAPI.konversationen({ status: statusFilter === 'ungelesen' ? 'offen' : statusFilter }); setKonvs((r.data || []).filter((k) => statusFilter !== 'ungelesen' || (k.ungelesen || 0) > 0)); }
    catch { toast.error('Konversationen konnten nicht geladen werden'); }
    setLaden(false);
  }, [statusFilter]);
  const ladeDetail = useCallback(async (id) => {
    try { const r = await superchatAPI.konversation(id); setDetail(r.data); setBearbeiteVorschlag(null); setTimeout(() => endeRef.current?.scrollIntoView({ block: 'end' }), 50); }
    catch { toast.error('Konversation konnte nicht geladen werden'); }
  }, []);
  useEffect(() => { ladeListe(); }, [ladeListe]);
  useEffect(() => { if (aktivId) ladeDetail(aktivId); }, [aktivId, ladeDetail]);
  useEffect(() => { teamAPI.list().then(r => setTeam(r.data?.users || r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    const t = setInterval(() => { ladeListe(); if (aktivId) ladeDetail(aktivId); }, 15000);
    return () => clearInterval(t);
  }, [ladeListe, ladeDetail, aktivId]);

  const konv = detail?.konversation;
  const vorschlag = detail?.vorschlag;
  const fensterOffen = konv?.fenster_offen !== false;
  const sichtbar = useMemo(() => {
    const s = suche.trim().toLowerCase();
    return konvs.filter(k => (!kanalFilter || k.kanal === kanalFilter) && (!s || (k.name || '').toLowerCase().includes(s) || (k.wa_id || '').includes(s) || (k.letzte_nachricht_vorschau || '').toLowerCase().includes(s)));
  }, [konvs, kanalFilter, suche]);
  const ungelesenGesamt = useMemo(() => konvs.reduce((n, k) => n + (k.ungelesen || 0), 0), [konvs]);

  const senden = async () => {
    if (!konv || (!text.trim() && !datei)) return;
    setSendet(true);
    try {
      if (datei) await superchatAPI.anhangSenden(konv.id, datei, text.trim());
      else await superchatAPI.senden(konv.id, text.trim());
      setText(''); setDatei(null); await ladeDetail(konv.id); ladeListe();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Versand fehlgeschlagen'); }
    finally { setSendet(false); }
  };
  const vorschlagAktion = async (aktion) => {
    if (!vorschlag) return;
    try {
      if (aktion === 'senden') { await superchatAPI.vorschlagFreigeben(vorschlag.id); toast.success('Gesendet'); }
      else if (aktion === 'bearbeitet_senden') { await superchatAPI.vorschlagFreigeben(vorschlag.id, bearbeiteVorschlag); toast.success('Gesendet (bearbeitet)'); }
      else { await superchatAPI.vorschlagVerwerfen(vorschlag.id); toast.success('Verworfen'); }
      await ladeDetail(konv.id); ladeListe();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Aktion fehlgeschlagen'); }
  };
  const aendern = async (daten, erfolg) => {
    try { await superchatAPI.konversationUpdate(konv.id, daten); if (erfolg) toast.success(erfolg); await ladeDetail(konv.id); ladeListe(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Änderung fehlgeschlagen'); }
  };
  const postfachAbrufen = async () => {
    setHoltMails(true);
    try { const d = (await superchatAPI.postfachAbrufen()).data; toast.success(d.uebernommen > 0 ? `${d.uebernommen} neue Kundenmail${d.uebernommen === 1 ? '' : 's'} übernommen, ${d.gefiltert} als Automat oder Werbung aussortiert` : `Keine neuen Kundenmails (${d.gefiltert} aussortiert)`); ladeListe(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Postfach konnte nicht abgerufen werden'); }
    finally { setHoltMails(false); }
  };
  const kontakteZuordnen = async () => {
    setOrdnetZu(true);
    try { const r = (await superchatAPI.konversationenZuordnen()).data; toast.success(`${r.verknuepft} Chats mit Leads verknüpft, ${r.benannt} benannt (${r.geprueft} geprüft)`); ladeListe(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Zuordnung fehlgeschlagen'); }
    finally { setOrdnetZu(false); }
  };
  const notizSpeichern = async () => {
    if (!notizText.trim()) return;
    await aendern({ notiz: notizText.trim() }, 'Notiz gespeichert');
    setNotizText(''); setNotizDialog(false);
  };

  // Nachrichten mit Datumstrennern
  const nachrichten = detail?.nachrichten || [];
  const composerHinweis = !konv ? '' : konv.kanal === 'email'
    ? `Antwort geht als E-Mail an ${konv.wa_id}, Betreff „Re: …“ der letzten Kundenmail.`
    : konv.kanal === 'whatsapp'
      ? (konv.bruecke_aktiv ? 'Geht als WhatsApp über die Studio-Nummer raus.' : 'Geht über die WhatsApp Cloud API raus.')
      : konv.kanal === 'instagram'
        ? (konv.instagram_verbunden ? 'Geht als Instagram-Nachricht raus (höchstens 1000 Zeichen). Bilder bitte in der Instagram-App senden.'
          : 'Instagram ist nicht verbunden (Agent › Anbindung). Antwort bitte in der Instagram-App.') : '';
  const kannSenden = darfSenden && konv && fensterOffen
    && (['whatsapp', 'email'].includes(konv.kanal) || (konv.kanal === 'instagram' && konv.instagram_verbunden));

  const Liste = (
    <div className={`w-full md:w-[340px] md:flex-none border-r border-border flex-col bg-background ${aktivId ? 'hidden md:flex' : 'flex'}`}>
      <div className="p-3 border-b border-border space-y-2">
        {/* Zeile 1: Titel + Zaehler; Zeile 2: Aktionen — in 340px passt beides nicht nebeneinander */}
        <div className="flex items-center justify-between gap-2 min-w-0">
          <h1 className="font-bold flex items-center gap-2 min-w-0"><MessageSquare className="h-4 w-4 flex-none" style={{ color: P1_ROT }} /><span className="truncate">Posteingang</span>{ungelesenGesamt > 0 && <Badge className="text-[10px] whitespace-nowrap flex-none">{ungelesenGesamt} neu</Badge>}</h1>
          <div className="flex items-center flex-none">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={popupUmschalten} title={popup ? 'Pop-up bei neuen Nachrichten: an (dieses Gerät)' : 'Pop-up bei neuen Nachrichten: aus (dieses Gerät)'} data-testid="inbox-popup">{popup ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5 text-muted-foreground" />}</Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={ladeListe} title="Aktualisieren" data-testid="inbox-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" disabled={holtMails} title="Neue Mails jetzt aus dem Postfach holen (läuft sonst alle 5 Minuten)" onClick={postfachAbrufen} data-testid="inbox-imap">{holtMails ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Inbox className="h-3.5 w-3.5" />} Mails abrufen</Button>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" disabled={ordnetZu} title="Chats per Rufnummer mit Leads und Mitgliedern verknüpfen" onClick={kontakteZuordnen} data-testid="inbox-zuordnen">{ordnetZu ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />} Kontakte verknüpfen</Button>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={suche} onChange={e => setSuche(e.target.value)} placeholder="Name, Nummer oder Text suchen" className="h-8 pl-8 text-sm" data-testid="inbox-suche" />
        </div>
        <div className="flex gap-1 flex-wrap text-xs">
          {[['offen', 'Posteingang'], ['ungelesen', 'Ungelesen'], ['geschlossen', 'Archiv'], ['alle', 'Alle']].map(([w, l]) => (
            <button key={w} onClick={() => setStatusFilter(w)} className={`rounded-full px-2.5 py-0.5 border ${statusFilter === w ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground'}`}>{l}</button>
          ))}
          <span className="w-px bg-border mx-1" />
          {[['', 'Alle Kanäle'], ['whatsapp', 'WhatsApp'], ['email', 'E-Mail'], ['instagram', 'Instagram']].map(([w, l]) => (
            <button key={w} onClick={() => setKanalFilter(w)} data-testid={`kanal-${w || 'alle'}`} className={`rounded-full px-2.5 py-0.5 border ${kanalFilter === w ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground'}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {laden && <p className="p-4 text-sm text-muted-foreground">Lade …</p>}
        {!laden && sichtbar.length === 0 && <p className="p-4 text-sm text-muted-foreground">{suche || kanalFilter ? 'Keine Konversation passt zu Suche oder Filter.' : statusFilter === 'offen' ? 'Nichts Offenes. Archivierte Chats stehen unter „Archiv".' : statusFilter === 'ungelesen' ? 'Alles gelesen.' : 'Noch keine Konversationen.'}</p>}
        {sichtbar.map((k) => (
          <button key={k.id} onClick={() => setAktivId(k.id)} data-testid={`konv-${k.id}`}
                  className={`w-full text-left px-3 py-2.5 border-b border-border/60 hover:bg-muted/60 transition-colors ${aktivId === k.id ? 'bg-muted' : ''}`}>
            <div className="flex items-center gap-2">
              <span className={`font-medium text-sm truncate flex-1 ${k.ungelesen > 0 ? 'font-semibold' : ''}`}>{k.name || k.wa_id}</span>
              <span className="text-[10px] text-muted-foreground flex-none">{fmtZeit(k.letzte_nachricht_am)}</span>
            </div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <KanalChip kanal={k.kanal} klein />
              {k.ungelesen > 0 && <Badge className="h-4 px-1.5 text-[10px]">{k.ungelesen} neu</Badge>}
              {k.hat_vorschlag && <Badge variant="outline" className="h-4 px-1.5 text-[10px] gap-0.5"><Bot className="h-2.5 w-2.5" /> KI-Vorschlag</Badge>}
              {k.status === 'geschlossen' && <Badge variant="secondary" className="h-4 px-1.5 text-[10px]">archiviert</Badge>}
              {!k.lead_id && <Badge variant="outline" className="h-4 px-1.5 text-[10px] text-amber-700 border-amber-300">ohne Lead</Badge>}
            </div>
            <p className="text-xs text-muted-foreground truncate mt-1">{k.letzte_nachricht_vorschau || '–'}</p>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden -m-4 md:-m-6" data-testid="posteingang">
      {Liste}
      <div className={`flex-1 flex-col min-w-0 ${aktivId ? 'flex' : 'hidden md:flex'}`}>
        {!konv ? (
          <div className="flex-1 flex items-center justify-center p-6 text-center">
            <div className="max-w-sm space-y-2">
              <MessageSquare className="h-8 w-8 mx-auto text-muted-foreground/50" />
              <p className="text-sm font-medium">Links eine Konversation auswählen</p>
              <p className="text-xs text-muted-foreground">WhatsApp kommt über die Studio-Nummer, E-Mails alle fünf Minuten aus dem Postfach (nur Kundenanliegen), Instagram über Meta. Antworten gehen direkt von hier raus.</p>
            </div>
          </div>
        ) : (
          <>
            {/* Kopfzeile */}
            <div className="px-3 md:px-4 py-2.5 border-b border-border bg-background flex items-center gap-2 flex-wrap">
              <button className="md:hidden p-1 -ml-1" onClick={() => setAktivId(null)} aria-label="Zurück zur Liste"><ArrowLeft className="h-5 w-5" /></button>
              <div className="min-w-[200px] flex-1">
                <p className="font-semibold text-sm truncate flex items-center gap-2">{konv.name || konv.wa_id} <KanalChip kanal={konv.kanal} klein /></p>
                <p className="text-xs text-muted-foreground truncate">{konv.kanal === 'email' ? konv.wa_id : konv.kanal === 'instagram' ? (konv.instagram_username ? `@${konv.instagram_username}` : 'Instagram') : `+${konv.wa_id}`}{konv.zugewiesen_an ? ` · zuständig: ${team.find(u => u.user_id === konv.zugewiesen_an)?.name || 'zugewiesen'}` : ''}</p>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {konv.lead_id
                  ? <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate(`/leads/${konv.lead_id}`)} data-testid="lead-oeffnen"><ExternalLink className="h-3.5 w-3.5" /> Lead öffnen</Button>
                  : <LeadVerknuepfen konvId={konv.id} onFertig={() => { ladeDetail(konv.id); ladeListe(); }} />}
                {konv.kanal !== 'instagram' && (isAdminPermissions || !!userPermissions?.['leads.edit']) && (
                  <ProbetrainingAusChat konv={konv} onFertig={() => { ladeDetail(konv.id); ladeListe(); }} />
                )}
                <select value={konv.zugewiesen_an || ''} onChange={e => aendern({ zugewiesen_an: e.target.value || null }, 'Zuständigkeit gespeichert')} className="h-8 rounded-md border border-input bg-background px-2 text-xs" title="Wer kümmert sich?">
                  <option value="">Niemand zuständig</option>
                  {team.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                </select>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setNotizDialog(true)}><StickyNote className="h-3.5 w-3.5" /> Notiz{konv.notizen?.length ? ` (${konv.notizen.length})` : ''}</Button>
                {konv.kanal === 'whatsapp' && (
                  <Button variant={konv.bot_pause ? 'default' : 'outline'} size="sm" className="gap-1.5" onClick={() => aendern({ bot_pause: !konv.bot_pause, ...(!konv.bot_pause ? { bot_pause_grund: 'Manuell pausiert' } : {}) })} data-testid="bot-pause-toggle" title="KI-Antworten für diesen Chat an/aus">
                    {konv.bot_pause ? <><Play className="h-3.5 w-3.5" /> KI wieder an</> : <><Pause className="h-3.5 w-3.5" /> KI aus</>}
                  </Button>
                )}
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => aendern({ status: konv.status === 'geschlossen' ? 'offen' : 'geschlossen' }, konv.status === 'geschlossen' ? 'Wieder geöffnet' : 'Als erledigt abgelegt')} data-testid="status-toggle">
                  {konv.status === 'geschlossen' ? <><ArchiveRestore className="h-3.5 w-3.5" /> Wieder öffnen</> : <><Archive className="h-3.5 w-3.5" /> Archivieren</>}
                </Button>
                {istLeitung && (
                  <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-red-600" title="Konversation samt Nachrichten und Anhängen endgültig löschen (Datenschutz)" data-testid="konv-loeschen"
                    onClick={async () => {
                      if (!window.confirm(`Konversation mit ${konv.name || konv.wa_id} samt aller Nachrichten und Anhänge endgültig löschen?`)) return;
                      try { const r = await superchatAPI.konversationLoeschen(konv.id); toast.success(`Gelöscht: ${r.data.nachrichten} Nachrichten, ${r.data.anhaenge} Anhänge`); setAktivId(null); setDetail(null); ladeListe(); }
                      catch (e) { toast.error(e?.response?.data?.detail || 'Löschen fehlgeschlagen'); }
                    }}><Trash2 className="h-3.5 w-3.5" /></Button>
                )}
              </div>
            </div>
            {konv.bot_pause && konv.kanal === 'whatsapp' && (
              <div className="px-4 py-1.5 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 text-xs border-b border-border flex items-center gap-1.5"><Info className="h-3.5 w-3.5" /> KI antwortet in diesem Chat nicht{konv.bot_pause_grund ? ` (${konv.bot_pause_grund})` : ''} — ein Mensch schreibt.</div>
            )}

            {/* Verlauf */}
            <div className="flex-1 overflow-y-auto px-3 md:px-4 py-3 space-y-2 bg-muted/30">
              {nachrichten.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">Noch keine Nachrichten.</p>}
              {nachrichten.map((n, i) => {
                const neuerTag = i === 0 || fmtTag(n.erstellt_am) !== fmtTag(nachrichten[i - 1].erstellt_am);
                const aus = n.richtung === 'aus';
                const absender = aus ? (n.von === 'agent' ? 'KI' : n.von === 'app' ? 'Handy' : n.von || 'CRM') : (n.von === 'notiz' ? `Notiz von ${n.erfasst_von || 'Team'}` : konv.name || 'Kunde');
                return (
                  <div key={n.id}>
                    {neuerTag && <div className="text-center my-3"><span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-background border border-border rounded-full px-2 py-0.5">{fmtTag(n.erstellt_am)}</span></div>}
                    <div className={`flex ${aus ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] md:max-w-[70%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words ${aus ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-background border border-border rounded-bl-md'}`}>
                        <div className={`text-[10px] mb-0.5 ${aus ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>{absender}</div>
                        {n.betreff && <div className="font-semibold mb-1">{n.betreff}</div>}
                        {(n.anhaenge || []).map(a => (
                          <div key={a.id} className="mb-1">
                            {a.art === 'bild' && <a href={anhangUrl(a)} target="_blank" rel="noreferrer"><img src={anhangUrl(a)} alt={a.name} className="max-h-64 rounded-lg" /></a>}
                            {a.art === 'audio' && <audio controls src={anhangUrl(a)} className="max-w-full h-9" />}
                            {a.art === 'video' && <video controls src={anhangUrl(a)} className="max-h-64 rounded-lg" />}
                            {a.art === 'dokument' && <a href={anhangUrl(a)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline"><Paperclip className="h-3 w-3" /> {a.name} <span className="opacity-70">({Math.round((a.groesse || 0) / 1024)} KB)</span></a>}
                          </div>
                        ))}
                        {n.transkribiert && <span className="text-[10px] opacity-70 mr-1" title="Sprachnachricht, automatisch in Text umgewandelt — die Aufnahme wird nicht gespeichert">🎤 Sprachnachricht:</span>}
                        {n.anhang_geloescht_am && <span className="text-[10px] italic opacity-70">Anhang nach Aufbewahrungsfrist gelöscht. </span>}
                        {n.text || (n.anhaenge?.length ? null : <span className="italic opacity-70">({n.typ})</span>)}
                        <div className={`text-[10px] mt-1 flex items-center gap-1.5 ${aus ? 'text-primary-foreground/70 justify-end' : 'text-muted-foreground'}`}>
                          {n.aufnahmegrund && <span className="opacity-80" title={AUFNAHME_TEXT(n.aufnahmegrund)}>{AUFNAHME_TEXT(n.aufnahmegrund)}</span>}
                          {n.von === 'app' && <Smartphone className="h-2.5 w-2.5" title="Vom Handy gesendet" />}
                          <span>{fmtZeit(n.erstellt_am)}</span>
                          {aus && <Haken status={n.status} />}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={endeRef} />
            </div>

            {/* KI-Vorschlag */}
            {vorschlag && darfSenden && (
              <div className="border-t border-border bg-background px-4 py-3" data-testid="vorschlag-banner">
                <div className="flex items-start gap-2">
                  <span className="mt-1 h-2.5 w-2.5 rounded-full flex-none" style={{ background: SICHERHEIT_FARBE[vorschlag.parsed?.sicherheit] || '#9ca3af' }} title={`Sicherheit der KI: ${vorschlag.parsed?.sicherheit || 'unbekannt'}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">KI-Antwortvorschlag, geht nur nach Klick raus</p>
                    {bearbeiteVorschlag === null ? <p className="text-sm">{vorschlag.parsed?.antwort || '(keine Antwort)'}</p>
                      : <textarea value={bearbeiteVorschlag} onChange={(e) => setBearbeiteVorschlag(e.target.value)} rows={3} autoFocus className="w-full text-sm rounded-md border border-border bg-background p-2" />}
                    <p className="text-xs text-muted-foreground mt-1"><Bot className="h-3 w-3 inline mr-1" />{vorschlag.parsed?.begruendung || 'KI-Vorschlag'}{(vorschlag.validierungsfehler || []).length > 0 && ` · Prüfhinweise: ${vorschlag.validierungsfehler.join(', ')}`}</p>
                  </div>
                  <div className="flex gap-1.5 flex-none flex-wrap justify-end">
                    {bearbeiteVorschlag === null ? (
                      <>
                        <Button size="sm" className="gap-1" onClick={() => vorschlagAktion('senden')} data-testid="vorschlag-senden"><Send className="h-3.5 w-3.5" /> Senden</Button>
                        <Button size="sm" variant="outline" className="gap-1" onClick={() => setBearbeiteVorschlag(vorschlag.parsed?.antwort || '')}><Pencil className="h-3.5 w-3.5" /> Bearbeiten</Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" className="gap-1" onClick={() => vorschlagAktion('bearbeitet_senden')}><Send className="h-3.5 w-3.5" /> Senden</Button>
                        <Button size="sm" variant="outline" onClick={() => setBearbeiteVorschlag(null)}>Abbrechen</Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" className="gap-1" onClick={() => vorschlagAktion('verwerfen')} data-testid="vorschlag-verwerfen"><X className="h-3.5 w-3.5" /> Verwerfen</Button>
                  </div>
                </div>
              </div>
            )}

            {/* Composer */}
            <div className="border-t border-border bg-background px-3 md:px-4 py-3 space-y-1.5">
              {!fensterOffen && konv.kanal === 'whatsapp' ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="fenster-zu"><Lock className="h-4 w-4 flex-none" /><span>Über die Cloud API sind nach 24 Stunden ohne Kundennachricht nur genehmigte Vorlagen erlaubt. Sobald der Kunde schreibt, ist das Feld wieder frei.</span></div>
              ) : !fensterOffen && konv.kanal === 'instagram' ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="fenster-zu"><Lock className="h-4 w-4 flex-none" /><span>Instagram erlaubt Antworten nur bis 24 Stunden nach der letzten Nachricht des Kunden. Sobald er wieder schreibt, ist das Feld frei.</span></div>
              ) : konv.kanal === 'instagram' && !konv.instagram_verbunden ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><Info className="h-4 w-4 flex-none" /><span>{composerHinweis}</span></div>
              ) : (
                <>
                  <div className="flex gap-2 items-end">
                    <input ref={dateiRef} type="file" accept="image/*,audio/*,video/mp4,application/pdf" className="hidden" onChange={(e) => { setDatei(e.target.files?.[0] || null); e.target.value = ''; }} />
                    <Button variant="outline" size="icon" className="flex-none" onClick={() => dateiRef.current?.click()} disabled={!kannSenden || sendet || konv.kanal === 'instagram'} title="Bild, PDF oder Audio anhängen (max. 15 MB)" data-testid="composer-anhang"><Paperclip className="h-4 w-4" /></Button>
                    <textarea value={text} onChange={(e) => setText(e.target.value)} rows={text.split('\n').length > 3 ? 4 : text.split('\n').length || 1}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); senden(); } }}
                      placeholder={!darfSenden ? 'Keine Sende-Berechtigung' : datei ? 'Bildunterschrift oder Text (optional)' : 'Nachricht schreiben … (Enter sendet, Shift+Enter neue Zeile)'}
                      disabled={!kannSenden || sendet} data-testid="composer-input"
                      className="flex-1 min-h-[40px] rounded-md border border-input bg-background px-3 py-2 text-sm resize-none" />
                    <Button onClick={senden} disabled={!kannSenden || sendet || (!text.trim() && !datei)} className="flex-none gap-1.5" data-testid="composer-send">{sendet ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Senden</Button>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
                    {datei && <span className="bg-muted rounded px-2 py-0.5 flex items-center gap-1 max-w-[220px] truncate">📎 {datei.name} <button onClick={() => setDatei(null)} aria-label="Anhang entfernen"><X className="h-3 w-3" /></button></span>}
                    <span>{composerHinweis}</span>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      <Dialog open={notizDialog} onOpenChange={setNotizDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Interne Notizen — sieht der Kunde nie</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {(konv?.notizen || []).length === 0 && <p className="text-sm text-muted-foreground">Noch keine Notizen.</p>}
            {(konv?.notizen || []).map((n, i) => (
              <div key={i} className="text-sm border-l-2 border-border pl-2"><p>{n.text}</p><p className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> {n.von} · {fmtZeit(n.am)}</p></div>
            ))}
          </div>
          <textarea value={notizText} onChange={(e) => setNotizText(e.target.value)} rows={3} placeholder="Neue Notiz" className="w-full text-sm rounded-md border border-border bg-background p-2" />
          <Button onClick={notizSpeichern} disabled={!notizText.trim()}>Notiz speichern</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
